import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../../services/api";
import {
  INSPECTION_FILE_UNAVAILABLE_MESSAGE,
  INSPECTION_FORBIDDEN_MESSAGE,
  INSPECTION_POLL_INTERVAL_MS,
  INSPECTION_SEARCH_ERROR_MESSAGE,
} from "../constants/ownerPortfolio";
import { fetchOwnerInspection, fetchOwnerInspectionFile } from "../services/ownerPortfolioService";
import { OwnerPatrimonyRequestStatus } from "../types/ownerDashboard.types";
import { InspectionCache } from "../types/ownerPortfolio.types";
import { shouldPollInspection } from "../utils/inspectionView";
import {
  closeInspectionTab,
  navigateInspectionTab,
  openBlankInspectionTab,
} from "../utils/openInspectionTab";
import { readApiErrorMessage, readApiStatus } from "../utils/readApiError";

interface UseOwnerInspectionOptions {
  kenloPropertyId: string;
  accessToken: string | null;
  enabled: boolean;
}

interface UseOwnerInspectionResult {
  cache: InspectionCache | null;
  status: OwnerPatrimonyRequestStatus;
  actionError: string | null;
  isOpening: boolean;
  retry: () => Promise<void>;
  openReport: () => Promise<void>;
}

export function useOwnerInspection({
  kenloPropertyId,
  accessToken,
  enabled,
}: UseOwnerInspectionOptions): UseOwnerInspectionResult {
  const [cache, setCache] = useState<InspectionCache | null>(null);
  const [status, setStatus] = useState<OwnerPatrimonyRequestStatus>("idle");
  const [actionError, setActionError] = useState<string | null>(null);
  const [isOpening, setIsOpening] = useState(false);
  const latestRequestIdRef = useRef(0);

  const loadInspection = useCallback(
    async (silent = false) => {
      if (!enabled || !accessToken || !kenloPropertyId) {
        return;
      }

      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;

      if (!silent) {
        setStatus("loading");
        setActionError(null);
      }

      try {
        api.defaults.headers.authorization = `Bearer ${accessToken}`;
        const response = await fetchOwnerInspection(kenloPropertyId);

        if (requestId !== latestRequestIdRef.current) {
          return;
        }

        setCache(response);
        setStatus("success");
      } catch (error) {
        if (requestId !== latestRequestIdRef.current || silent) {
          return;
        }

        const httpStatus = readApiStatus(error);
        setActionError(
          httpStatus === 403
            ? readApiErrorMessage(error, INSPECTION_FORBIDDEN_MESSAGE)
            : readApiErrorMessage(error, INSPECTION_SEARCH_ERROR_MESSAGE)
        );
        setStatus("error");
      }
    },
    [accessToken, enabled, kenloPropertyId]
  );

  useEffect(() => {
    if (!enabled || !accessToken || !kenloPropertyId) {
      return;
    }

    // One GET searches, stores the result, and answers. A repeat inside 10 minutes returns that result.
    void loadInspection();
  }, [accessToken, enabled, kenloPropertyId, loadInspection]);

  useEffect(() => {
    if (!enabled || !shouldPollInspection(cache?.status ?? null)) {
      return;
    }

    // The opening read already searched. Polling only continues while that search is still running.
    const timer = window.setInterval(() => {
      void loadInspection(true);
    }, INSPECTION_POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [cache?.status, enabled, loadInspection]);

  const openReport = useCallback(async () => {
    if (!accessToken || !kenloPropertyId || isOpening) {
      return;
    }

    const tab = openBlankInspectionTab();
    setIsOpening(true);
    setActionError(null);

    try {
      api.defaults.headers.authorization = `Bearer ${accessToken}`;
      const file = await fetchOwnerInspectionFile(kenloPropertyId);
      navigateInspectionTab(tab, file.url);
    } catch (error) {
      closeInspectionTab(tab);
      const httpStatus = readApiStatus(error);
      setActionError(
        httpStatus === 403
          ? readApiErrorMessage(error, INSPECTION_FORBIDDEN_MESSAGE)
          : readApiErrorMessage(error, INSPECTION_FILE_UNAVAILABLE_MESSAGE)
      );
    } finally {
      setIsOpening(false);
    }
  }, [accessToken, isOpening, kenloPropertyId]);

  return {
    cache,
    status,
    actionError,
    isOpening,
    retry: () => loadInspection(),
    openReport,
  };
}
